import { MAD_MONKEY_MAPS } from "../madmonkey/hostelMaps";
import { partnerAsset } from "../shared/assets";
import indewaag from "./indewaag.json";
import indewaagNl from "./indewaag-nl.json";
import odeaandeamstel from "./odeaandeamstel.json";
import type { PartnerMapContent } from "./types";
import urbanJungle from "./urban-jungle.json";
import viajero from "./viajero-mexico-cdmx.json";

/**
 * Every partner map page. A new partner is its JSON file in this folder, one line in `FILES` below and one entry in
 * `headIndex.ts`; `registry.test.ts` checks each file against the content type and the two against each other.
 * The files hold media paths; they become URLs here. The Mad Monkey hostels have no file each: their maps are made from
 * the hostel data (`madmonkey/hostelMaps.ts`), their heads too (`madmonkey/routeHead.ts`, behind `headIndex.ts`).
 */
const FILES = [urbanJungle, indewaag, indewaagNl, odeaandeamstel, ...MAD_MONKEY_MAPS] as unknown as PartnerMapContent[];

function withMedia(content: PartnerMapContent): PartnerMapContent {
	const url = (path: string) => partnerAsset(content.media ?? content.slug, path);
	const withPhoto = <T extends { photo?: string }>(item: T): T => (item.photo ? { ...item, photo: url(item.photo) } : item);
	return {
		...content,
		head: { ...content.head, ogImage: url(content.head.ogImage) },
		partner: { ...content.partner, logo: url(content.partner.logo) },
		header: content.header.wordmark ? { ...content.header, wordmark: url(content.header.wordmark) } : content.header,
		base: { ...content.base, photos: content.base.photos.map(url) },
		highlights: content.highlights.map(withPhoto),
		// a venue may carry a logo of its own, drawn on its marker
		venues: content.venues.map((venue) => withPhoto(venue.logo ? { ...venue, logo: url(venue.logo) } : venue)),
		weekProgram: content.weekProgram && {
			...content.weekProgram,
			// an event's list thumbnail (`image`) is a media path in a content file too
			events: content.weekProgram.events.map((e) => withPhoto(e.image && !/^https?:/.test(e.image) ? { ...e, image: url(e.image) } : e)),
			...(content.weekProgram.tours && { tours: content.weekProgram.tours.map((tour) => (tour.image ? { ...tour, image: url(tour.image) } : tour)) }),
		},
		nextStops: content.nextStops && { ...content.nextStops, stops: content.nextStops.stops.map((stop) => ({ ...stop, photo: url(stop.photo) })) },
		offers: content.offers && { ...content.offers, cards: content.offers.cards.map((card) => ({ ...card, photo: url(card.photo) })) },
		contact: content.contact?.sister
			? {
					...content.contact,
					sister: {
						...content.contact.sister,
						logo: url(content.contact.sister.logo),
						// its logo for a white ground is a media path too
						...(content.contact.sister.logoOnLight && { logoOnLight: url(content.contact.sister.logoOnLight) }),
					},
				}
			: content.contact,
	};
}

export const PARTNER_MAPS: Record<string, PartnerMapContent> = Object.fromEntries(FILES.map((content) => [content.slug, withMedia(content)]));

/**
 * Built and QA'd, but not served: Viajero CDMX Centro (Stippl#1612) waits until Viajero agrees; the pitch is Ilse's prototype on
 * demo.stippl.io/viajero-cdmx.html (Robin, 2026-10-19). Tests still check it. To publish: move it into `FILES` above and uncomment
 * its entry in `headIndex.ts`.
 */
export const HIDDEN_PARTNER_MAPS: Record<string, PartnerMapContent> = { "viajero-mexico-cdmx": withMedia(viajero as unknown as PartnerMapContent) };

export const getPartnerMap = (slug: string): PartnerMapContent | undefined =>
	Object.prototype.hasOwnProperty.call(PARTNER_MAPS, slug) ? PARTNER_MAPS[slug] : undefined;
